import { useState, useEffect } from 'react';
import { Bell, X, Zap } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

interface PushNotificationPromptProps {
  /** When true, shows an inline card instead of a floating overlay */
  inline?: boolean;
  /** If set, only show when user has cycle tracking active */
  requiresCycle?: boolean;
  cyclePhase?: string | null;
}

export function PushNotificationPrompt({
  inline = false,
  requiresCycle = false,
  cyclePhase = null,
}: PushNotificationPromptProps) {
  const { user } = useAuth();
  const { permission, isSupported, isSubscribed, isLoading, requestPermission } = usePushNotifications();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [success, setSuccess] = useState(false);

  const hasCycle = !requiresCycle || !!cyclePhase;

  useEffect(() => {
    if (!user || !isSupported || dismissed || !hasCycle) return;
    const alreadyDismissed = localStorage.getItem('push_prompt_dismissed_v2');
    if (alreadyDismissed) return;

    if (inline) {
      // Inline variant shows immediately
      if (permission === 'default' && !isSubscribed) setShow(true);
    } else {
      // Floating variant — show after 45 seconds
      const timer = setTimeout(() => {
        if (permission === 'default' && !isSubscribed) setShow(true);
      }, 45_000);
      return () => clearTimeout(timer);
    }
  }, [user, permission, isSupported, isSubscribed, dismissed, hasCycle, inline]);

  const handleEnable = async () => {
    const ok = await requestPermission();
    if (ok) {
      setSuccess(true);
      setTimeout(() => setShow(false), 2500);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('push_prompt_dismissed_v2', 'true');
    setDismissed(true);
    setShow(false);
  };

  if (!show || dismissed || isSubscribed || permission !== 'default') return null;

  // ── Success state ──────────────────────────────────────────────────────────
  if (success) {
    return inline ? (
      <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 flex items-center gap-3">
        <Zap className="h-4 w-4 text-primary shrink-0" />
        <p className="text-xs text-primary font-medium">Daily tip enabled — see you tomorrow morning.</p>
      </div>
    ) : null;
  }

  // ── Inline card (shown on AI Report page) ─────────────────────────────────
  if (inline) {
    return (
      <div className={cn(
        'rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3.5 space-y-2.5',
      )}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary shrink-0" />
            <p className="text-xs font-semibold text-foreground uppercase tracking-wide">
              Get your daily tip
            </p>
          </div>
          <button onClick={handleDismiss} className="text-muted-foreground/50 hover:text-muted-foreground transition-colors shrink-0">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {cyclePhase
            ? `Every morning: a personalised training or nutrition tip based on your ${cyclePhase} phase and your exact scan numbers. No generic advice.`
            : 'Every morning: a personalised health tip based on your exact scan numbers and phase angle. No generic advice.'}
        </p>

        <button
          onClick={handleEnable}
          disabled={isLoading}
          className={cn(
            'text-xs font-semibold text-primary hover:text-primary/70 transition-colors',
            isLoading && 'opacity-50',
          )}
        >
          {isLoading ? 'Enabling…' : 'Enable daily tip →'}
        </button>
      </div>
    );
  }

  // ── Floating overlay (global, after 45s) ──────────────────────────────────
  return (
    <div className="fixed bottom-24 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 premium-card rounded-xl p-4 shadow-2xl animate-fade-in">
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
          <Bell className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-foreground font-semibold text-sm">
            {cyclePhase ? 'Your daily cycle tip' : 'Stay on track'}
          </h4>
          <p className="text-muted-foreground text-xs mt-1 leading-relaxed">
            {cyclePhase
              ? `Get a personalised ${cyclePhase}-phase training or nutrition tip every morning, built from your actual scan numbers.`
              : 'Get a daily personalised tip each morning — based on your phase angle, body composition, and health priority.'}
          </p>
          <button
            onClick={handleEnable}
            disabled={isLoading}
            className={cn(
              'mt-3 text-xs font-bold text-background bg-primary px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors',
              isLoading && 'opacity-50',
            )}
          >
            {isLoading ? 'Enabling…' : 'Enable daily tips'}
          </button>
        </div>
      </div>
    </div>
  );
}
